/**
 * Thrown by the global fetch interceptor when a 401 is detected.
 * Callers should check `instanceof AuthInterceptedError` in their catch blocks
 * and return silently — the global error dialog handles user-facing messaging.
 */
export class AuthInterceptedError extends Error {
  constructor() {
    super('auth_intercepted');
    this.name = 'AuthInterceptedError';
  }
}

/**
 * Global Fetch Interceptor
 * Patches the native fetch function to add global error handling for token expiration
 * This works across the ENTIRE application without modifying individual API functions
 */

type FetchErrorListener = (error: {
  title: string;
  message: string;
  isAuthError: boolean;
}) => void;

let globalErrorListener: FetchErrorListener | null = null;
let interceptorInstalled = false;

function getRequestUrl(input: RequestInfo | URL) {
  return typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;
}

function isApiRequest(input: RequestInfo | URL) {
  return /\/api\//i.test(getRequestUrl(input));
}

function isLogoutRequest(input: RequestInfo | URL) {
  return /\/api\/rfi\/auth\/logout(?:$|[?#/])/i.test(getRequestUrl(input));
}

function isLikelyAuthRedirect(response: Response, input: RequestInfo | URL) {
  if (!isApiRequest(input)) {
    return false;
  }

  if (response.redirected && /login|authorize|oauth|signin|auth/i.test(response.url)) {
    return true;
  }

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  return response.status === 200 && contentType.includes('text/html');
}

/**
 * Detects if response contains "Invalid or expired bearer token" error
 */
async function isTokenExpiredError(response: Response): Promise<boolean> {
  // 401 Unauthorized is the primary indicator
  if (response.status === 401) {
    return true;
  }

  // Also check response body for token error messages
  try {
    const body = (await response.clone().json()) as Record<string, unknown>;
    const message = String(body?.message || body?.error || '').toLowerCase();
    return message.includes('invalid') && message.includes('bearer token');
  } catch {
    return false;
  }
}

function notifySessionExpired() {
  globalErrorListener?.({
    title: 'Session Expired',
    message: 'Your session has expired. Please re-authenticate to continue.',
    isAuthError: true,
  });
}

/**
 * Set the callback for global fetch errors
 */
export const setGlobalFetchErrorListener = (listener: FetchErrorListener | null) => {
  globalErrorListener = listener;
};

/**
 * Install the global fetch interceptor
 * Call this during app initialization
 */
export const installFetchInterceptor = () => {
  if (interceptorInstalled || typeof window === 'undefined') {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  interceptorInstalled = true;

  window.fetch = (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    if (isLogoutRequest(input)) {
      return originalFetch(input, init);
    }

    return originalFetch(input, init)
      .then(async (response: Response) => {
        if (isLikelyAuthRedirect(response, input)) {
          notifySessionExpired();
          return response;
        }

        // Check for token expiration errors and notify global listener
        if (await isTokenExpiredError(response)) {
          notifySessionExpired();
          throw new AuthInterceptedError();
        }

        // Log other non-ok responses (but don't block them)
        // Individual API functions will handle their own error handling
        if (!response.ok) {
          // Only trigger global error for server errors (5xx) and some client errors
          // Skip 404, 400 as these might be handled locally
          if (response.status !== 404 && response.status !== 400) {
            try {
              const cloned = response.clone();
              const body = (await cloned.json()) as Record<string, unknown>;
              const errorMessage = String(body?.message || `HTTP ${response.status}`);

              // Only show global error if it's not a client-side validation error
              if (!errorMessage.toLowerCase().includes('validation')) {
                globalErrorListener?.({
                  title: 'Request Failed',
                  message: errorMessage,
                  isAuthError: false,
                });
              }
            } catch {
              // If we can't parse the error, it's probably a network error
              if (response.status >= 500) {
                globalErrorListener?.({
                  title: 'Server Error',
                  message: `HTTP ${response.status}: ${response.statusText}`,
                  isAuthError: false,
                });
              }
            }
          }
        }

        return response;
      })
      .catch((error: Error) => {
        // Network errors
        globalErrorListener?.({
          title: 'Network Error',
          message: error instanceof Error ? error.message : 'Failed to connect to the server',
          isAuthError: false,
        });
        throw error;
      });
  };
};

